/* Stran "Napake" — moje prijave in (za superadmina) vse prijave s triažo.
   [PATCH napake-fe v2]

   Zavihek "Vse prijave" se pokaže samo, če /napake/stevec vrne je_superadmin.
   Priloge se prenašajo z avtoriziranim klicem kot blob — <img src> brez glave
   ne bi delal, ker je endpoint zaščiten. */
import React, { useEffect, useState } from 'react';
import NapakeObvescanje from '../components/napake/NapakeObvescanje'; // [PATCH napake-email-fe v1]
import { Loader2, Save, Trash2, ImagePlus, X } from 'lucide-react'; // [PATCH napake-slike-filter v1] ImagePlus, X
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { api } from '../lib/api';
import { useNapakeStore } from '../stores/napakeStore';

const STATUSI = ['nova', 'v_obravnavi', 'reseno', 'zavrnjeno', 'koncano']; // [PATCH napake-dialog-fe v2]

const STATUS_LABEL: Record<string, string> = {
  nova: 'Nova',
  v_obravnavi: 'V obravnavi',
  reseno: 'Rešeno',
  zavrnjeno: 'Zavrnjeno',
  koncano: 'Končano', // [PATCH napake-dialog-fe v2]
};

const STATUS_BARVA: Record<string, string> = {
  nova: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  v_obravnavi: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
  reseno: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  zavrnjeno: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  koncano: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200', // [PATCH napake-dialog-fe v2]
};

/* [PATCH napake-obseg-fe v1] Paleta znack prijaviteljev.

   Razredi so zapisani DOBESEDNO: Tailwind JIT vkljuci samo tisto, kar vidi
   v izvorni kodi — `bg-${x}-100` bi dal brezbarvno znacko.

   V paleti NAMENOMA ni modre in jantarne: ti dve ze nosita pomen na vrstici
   seznama (modra = izbrana prijava, jantarna = neprebran odgovor), tretji
   pomen bi ju zlil.

   Indeks pride s streznika (prijavitelj_barva) in izhaja iz polozaja
   uporabnika v seznamu, urejenem po created_at — ne iz hasha. Dolzina
   palete mora ostati usklajena s PALETA_PRIJAVITELJEV v backend/napake.py. */
const PRIJAVITELJ_BARVE = [
  'bg-violet-100 text-violet-700 dark:bg-violet-900 dark:text-violet-300',
  'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300',
  'bg-rose-100 text-rose-700 dark:bg-rose-900 dark:text-rose-300',
  'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300',
  'bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-300',
  'bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900 dark:text-fuchsia-300',
  'bg-lime-100 text-lime-700 dark:bg-lime-900 dark:text-lime-300',
  'bg-cyan-100 text-cyan-700 dark:bg-cyan-900 dark:text-cyan-300',
];

const barvaPrijavitelja = (i: any) => (
  typeof i === 'number' && i >= 0
    ? PRIJAVITELJ_BARVE[i % PRIJAVITELJ_BARVE.length]
    : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
);

/* [PATCH napake-prioriteta-fe v1] 1 = najvišja. To je EDINO, kar o vrstnem redu
   reševanja vidi prijavitelj — mesta v čakalni vrsti mu namenoma ne kažemo. */
const PRIORITETE = [1, 2, 3, 4];

/* [PATCH napake-prioriteta-fe v2] Stopnja 0 = ŠE NEOBDELANO.
   Namenoma ločeno od "Srednja": prijavitelj mora videti razliko med
   »še nismo pogledali« in »ocenjeno kot srednje nujno«. Ker `prio()` odslej
   vrača 0 in slovarja dobita ključ 0, vse obstoječe uporabe
   PRIO_LABEL[prio(...)] delujejo brez sprememb. */
const PRIO_LABEL: Record<number, string> = {
  0: 'Neobdelano',
  1: 'Kritična', 2: 'Visoka', 3: 'Srednja', 4: 'Nizka',
};

const PRIO_BARVA: Record<number, string> = {
  0: 'bg-white dark:bg-gray-900 text-gray-500 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-600',
  1: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  2: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300',
  3: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
  4: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
};

const prio = (x: any) => {
  if (x === null || x === undefined || x === '') return 0;
  const n = Number(x);
  return n >= 1 && n <= 4 ? n : 0;
};

/* [PATCH napake-filtri-fe v1] Preklop vrednosti v seznamu izbir.
   Vedno vrne NOV seznam — s tem se sproži useEffect, ki znova naloži podatke. */
const preklopi = (seznam: string[], v: string) =>
  seznam.indexOf(v) >= 0 ? seznam.filter((x) => x !== v) : seznam.concat([v]);

const fmtCas = (iso: string) => { // [PATCH napake-cas-berlin v1] UTC iz strežnika -> Europe/Berlin
  if (!iso) return '';
  const s = /([zZ]|[+-]\d\d:?\d\d)$/.test(iso) ? iso : iso + 'Z';
  const d = new Date(s);
  if (isNaN(d.getTime())) return iso;
  const deli = new Intl.DateTimeFormat('sl-SI', {
    timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(d);
  const g = (t: string) => (deli.find((x) => x.type === t) || { value: '' }).value;
  return g('day') + '.' + g('month') + '.' + g('year') + ' ' + g('hour') + ':' + g('minute');
};

const inp = 'w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:outline-none focus:border-blue-400';
const lbl = 'block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1';

/* [PATCH napake-slike-filter v1] slika sporočila: avtoriziran prenos kot blob (endpoint je zaščiten) */
function SlikaPriloge({ pid, aid }: { pid: string; aid: string }) {
  const [url, setUrl] = useState<string>('');
  const [napaka, setNapaka] = useState(false);
  useEffect(() => {
    let u = '';
    let ziv = true;
    api.get('/napake/' + pid + '/priloga/' + aid, { responseType: 'blob' })
      .then((r: any) => { u = URL.createObjectURL(r.data); if (ziv) setUrl(u); })
      .catch(() => { if (ziv) setNapaka(true); });
    return () => { ziv = false; if (u) { try { URL.revokeObjectURL(u); } catch { /* tiho */ } } };
  }, [pid, aid]);
  if (napaka) return <div className="px-2 py-4 text-[11px] text-gray-400 border border-dashed border-gray-200 dark:border-gray-700 rounded-lg text-center">Slike ni bilo mogoče naložiti</div>;
  if (!url) return <div className="h-24 rounded-lg bg-gray-100 dark:bg-gray-800 animate-pulse" />;
  return (
    <a href={url} target="_blank" rel="noreferrer" className="block border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white">
      <img src={url} alt="slika" className="w-full max-h-48 object-contain" />
    </a>);
}

/* [PATCH napake-slike-filter v1] predogled slike pred pošiljanjem */
function PredogledSlike({ f, odstrani }: { f: File; odstrani: () => void }) {
  const [url, setUrl] = useState('');
  useEffect(() => { const u = URL.createObjectURL(f); setUrl(u); return () => { try { URL.revokeObjectURL(u); } catch { /* tiho */ } }; }, [f]);
  return (
    <div className="relative w-24 h-24 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white">
      {url && <img src={url} alt={f.name} className="w-full h-full object-cover" />}
      <button type="button" onClick={odstrani} title="Odstrani"
        className="absolute top-1 right-1 p-0.5 rounded-full bg-white/90 text-gray-600 hover:text-red-600 shadow"><X className="w-3.5 h-3.5" /></button>
    </div>);
}

export function NapakePage() {
  const jeSuperadmin = useNapakeStore((s) => s.jeSuperadmin);
  const vidiServis = useNapakeStore((s) => s.vidiServis); // [PATCH napake-obseg-fe v1]
  const naloziStevec = useNapakeStore((s) => s.nalozi);
  const odpriModal = useNapakeStore((s) => s.odpri);
  const anketa = useNapakeStore((s) => s.anketa); // [PATCH napake-dialog-fe v4] vsaka anketa stevca osvezi seznam

  /* [PATCH napake-dialog-fe v3] superadmin zacne na "Vse prijave" (zavihka
     Moje prijave nima); ob hladnem zagonu vlogo prinese stevec — glej effect
     spodaj. */
  const [zavihek, setZavihek] = useState(jeSuperadmin ? 'vse' : 'moje');
  // [PATCH napake-filtri-fe v1] veckratna izbira; prazen seznam = brez filtra
  /* [PATCH napake-slike-filter v1] admin (superadmin, admin/računovodja servisa) privzeto vidi samo odprte prijave */
  const ODPRTI = ['nova', 'v_obravnavi'];
  const [filterStatus, setFilterStatus] = useState<string[]>(() => (jeSuperadmin || vidiServis ? ODPRTI : []));
  const filterDotaknjen = React.useRef(false);
  const nastaviStatus = (v: string[]) => { filterDotaknjen.current = true; setFilterStatus(v); };
  const [filterPrio, setFilterPrio] = useState<string[]>([]);
  // [PATCH napake-obseg-fe v1] privzeto ves servis; cip zozi na lastne prijave
  const [samoMoje, setSamoMoje] = useState(false);
  const [seznam, setSeznam] = useState<any[]>([]);
  const [nalagam, setNalagam] = useState(true);
  const [verzija, setVerzija] = useState(0);

  const [izbranId, setIzbranId] = useState('');
  const [podrobno, setPodrobno] = useState<any>(null);
  const [slikeUrl, setSlikeUrl] = useState<string[]>([]);
  const [nalagamPodrobno, setNalagamPodrobno] = useState(false);

  /* triažna polja (samo superadmin) */
  const [tStatus, setTStatus] = useState('');
  const [tPrioriteta, setTPrioriteta] = useState(0); // [PATCH napake-prioriteta-fe v2] privzeto neobdelano
  const [tOpombe, setTOpombe] = useState('');
  /* [PATCH napake-dialog-fe v1] skupni composer niti (prijavitelj IN
     superadmin); triazna textarea "Odgovor prijavitelju" je odstranjena —
     vsi odgovori gredo v nit sporocil. */
  const [novoSporocilo, setNovoSporocilo] = useState('');
  const [noveSlike, setNoveSlike] = useState<File[]>([]); /* [PATCH napake-slike-filter v1] slike k sporočilu (izbira ali Ctrl+V) */
  const izbiraSlik = React.useRef<HTMLInputElement>(null);
  const dodajSlike = (fs: File[]) => setNoveSlike((s) => s.concat(fs.filter((f) => /^image\//.test(f.type))).slice(0, 5));
  const [posiljam, setPosiljam] = useState(false);
  const [shranjujem, setShranjujem] = useState(false);

  /* ob vstopu osveži števec (da se značka uskladi) */
  useEffect(() => { naloziStevec(); }, [naloziStevec]);

  /* [PATCH napake-dialog-fe v3] hladen zagon: vloga pride asinhrono s
     stevcem — superadmina premaknemo z 'moje' na 'vse'. Nazaj ne more,
     ker je gumb zavihka skrit; effect se zato izvede najvec enkrat. */
  useEffect(() => {
    if (jeSuperadmin && zavihek === 'moje') setZavihek('vse');
  }, [jeSuperadmin, zavihek]);
  /* [PATCH napake-slike-filter v1] vloga pride asinhrono — privzeti filter nastavi enkrat, dokler ga uporabnik ni spremenil */
  useEffect(() => {
    if ((jeSuperadmin || vidiServis) && !filterDotaknjen.current) setFilterStatus(['nova', 'v_obravnavi']);
  }, [jeSuperadmin, vidiServis]);

  /* seznam */
  useEffect(() => {
    let odpovedan = false;
    setNalagam(true);
    // [PATCH napake-filtri-fe v1] izbrane vrednosti gredo z vejico;
    // prazen seznam pomeni, da parametra sploh ne posljemo (= brez filtra)
    const par: string[] = [];
    if (filterStatus.length > 0) {
      par.push('status=' + encodeURIComponent(filterStatus.join(',')));
    }
    if (filterPrio.length > 0) {
      par.push('prioriteta=' + encodeURIComponent(filterPrio.join(',')));
    }
    // [PATCH napake-obseg-fe v1] Obseg gre na STREZNIK, ne v brskalnik:
    // seznam je odrezan na 200 vrstic in filtriranje v brskalniku bi pri
    // vecjem servisu tiho skrivalo zapise. Brez parametra /napake/moje vrne
    // ves servis (admin, racunovodja) oziroma samo lastne prijave (stranka).
    if (samoMoje) {
      par.push('obseg=moje');
    }
    // [PATCH napake-filtri2-fe v1] status in stopnja veljata na OBEH zavihkih.
    const osnova = zavihek === 'vse' ? '/napake/vse' : '/napake/moje';
    const pot = osnova + (par.length > 0 ? '?' + par.join('&') : '');
    api.get(pot)
      .then((r: any) => {
        if (odpovedan) return;
        setSeznam(Array.isArray(r.data) ? r.data : []);
      })
      .catch(() => {
        if (!odpovedan) toast.error('Napaka pri nalaganju prijav');
      })
      .finally(() => { if (!odpovedan) setNalagam(false); });
    return () => { odpovedan = true; };
  }, [zavihek, filterStatus, filterPrio, samoMoje, verzija, anketa]); // [PATCH napake-prioriteta-fe v2] + [PATCH napake-dialog-fe v4] + [PATCH napake-obseg-fe v1]

  /* podrobnosti + priloge */
  useEffect(() => {
    let odpovedan = false;
    let ustvarjeni: string[] = [];
    if (!izbranId) {
      setPodrobno(null);
      setSlikeUrl([]);
      return undefined;
    }
    setNalagamPodrobno(true);
    (async () => {
      try {
        const r = await api.get('/napake/' + izbranId);
        if (odpovedan) return;
        const p = r.data || {};
        setPodrobno(p);
        setTStatus(p.status || '');
        setTPrioriteta(prio(p.prioriteta)); // [PATCH napake-prioriteta-fe v1]
        setTOpombe(p.interne_opombe || '');
        const priloge = p.priloge || [];
        for (let i = 0; i < priloge.length; i++) {
          try {
            const rb = await api.get('/napake/' + izbranId + '/priloga/' + priloge[i].id,
                                     { responseType: 'blob' });
            if (odpovedan) return;
            ustvarjeni = ustvarjeni.concat([URL.createObjectURL(rb.data)]);
          } catch {
            ustvarjeni = ustvarjeni.concat(['']);
          }
          setSlikeUrl(ustvarjeni.slice());
        }
        /* ogled je označil prijavo kot prebrano -> osveži značko */
        naloziStevec();
      } catch {
        if (!odpovedan) toast.error('Napaka pri nalaganju podrobnosti');
      } finally {
        if (!odpovedan) setNalagamPodrobno(false);
      }
    })();
    return () => {
      odpovedan = true;
      ustvarjeni.forEach((u) => {
        if (u) { try { URL.revokeObjectURL(u); } catch { /* tiho */ } }
      });
    };
  }, [izbranId, verzija, naloziStevec]);

  const shraniTriazo = async () => {
    if (!izbranId) return;
    setShranjujem(true);
    try {
      await api.patch('/napake/' + izbranId, {
        status: tStatus,
        prioriteta: tPrioriteta, // [PATCH napake-prioriteta-fe v1]
        interne_opombe: tOpombe,
      });
      toast.success('Shranjeno.');
      setVerzija((v) => v + 1);
      naloziStevec();
    } catch (e: any) {
      const d = e && e.response && e.response.data && e.response.data.detail;
      toast.error(typeof d === 'string' ? d : 'Napaka pri shranjevanju');
    } finally {
      setShranjujem(false);
    }
  };

  /* [PATCH napake-dialog-fe v1] posiljanje sporocila v nit. Po uspehu
     osvezitev prek obstojecega vzorca (verzija+1) + stevec za znacko. */
  const posljiSporocilo = async () => {
    const vsebina = novoSporocilo.trim();
    if (!izbranId || (!vsebina && noveSlike.length === 0)) return;
    setPosiljam(true);
    try {
      if (noveSlike.length > 0) { /* [PATCH napake-slike-filter v1] */
        const fd = new FormData();
        fd.append('vsebina', vsebina);
        noveSlike.forEach((f) => fd.append('datoteke', f));
        const r: any = await api.post('/napake/' + izbranId + '/sporocilo-slike', fd);
        if (r?.data?.opozorila?.length) toast.error('Nekaterih slik ni bilo mogoče shraniti: ' + r.data.opozorila.join('; '));
        setNoveSlike([]);
      } else {
        await api.post('/napake/' + izbranId + '/sporocilo', { vsebina });
      }
      setNovoSporocilo('');
      setVerzija((v) => v + 1);
      naloziStevec();
    } catch (e: any) {
      const d = e && e.response && e.response.data && e.response.data.detail;
      toast.error(typeof d === 'string' ? d : 'Sporočila ni bilo mogoče poslati');
    } finally {
      setPosiljam(false);
    }
  };

  const zamenjajZavihek = (z: string) => {
    setZavihek(z);
    setIzbranId('');
    setPodrobno(null);
    setSlikeUrl([]);
  };

  const gumbZavihka = (z: string, napis: string) => (
    <button onClick={() => zamenjajZavihek(z)}
      className={clsx(
        'px-3 py-2 text-sm font-medium border-b-2 -mb-px transition',
        zavihek === z
          ? 'border-blue-600 text-blue-600 dark:text-blue-400'
          : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
      )}>
      {napis}
    </button>
  );

  const K = (podrobno && podrobno.kontekst) || {};
  const klici = K.zadnji_klici || [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Prijave napak</h1>
        <NapakeObvescanje /* [PATCH napake-email-fe v1] */ />
        <button onClick={odpriModal}
          className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition">
          Prijavi napako
        </button>
      </div>

      <div className="flex items-center gap-2 border-b border-gray-200 dark:border-gray-800">
        {/* [PATCH napake-dialog-fe v3] superadmin vidi samo "Vse prijave" */}
        {/* [PATCH napake-obseg-fe v1] naslov pove resnico o obsegu */}
        {!jeSuperadmin && gumbZavihka('moje', vidiServis ? 'Prijave servisa' : 'Moje prijave')}
        {jeSuperadmin && gumbZavihka('vse', 'Vse prijave')}
      </div>

      {/* [PATCH napake-filtri2-fe v1] Filtra sta v DVEH vrsticah — stopnja, nato
          status — in odslej na OBEH zavihkih: prijavitelj ima na svojem seznamu
          enake možnosti kot superadmin na svojem. Devet čipov v eni vrstici se je
          lomilo čez širino in oba filtra sta se zlila v eno kašo; oznaka fiksne
          širine (w-16) zdaj poravna čipe navpično.
          Prazen izbor pomeni "vse". Osveževanje ostaja ročno — samodejno bi med
          tipkanjem povozilo triazna polja. */}
      <div className="space-y-2">
        {/* [PATCH napake-obseg-fe v1] Cip obsega. Viden samo, kadar uporabnik
            sploh vidi ves servis — stranka ima obseg vedno enak. */}
        {vidiServis && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="w-16 shrink-0 text-xs text-gray-500 dark:text-gray-400">Obseg:</span>
            <button onClick={() => setSamoMoje(false)}
              className={clsx('px-2 py-1 rounded-lg text-xs font-medium border transition',
                !samoMoje
                  ? 'bg-blue-600 border-blue-600 text-white'
                  : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-blue-400')}>
              Ves servis
            </button>
            <button onClick={() => setSamoMoje(true)}
              className={clsx('px-2 py-1 rounded-lg text-xs font-medium border transition',
                samoMoje
                  ? 'bg-blue-600 border-blue-600 text-white'
                  : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-blue-400')}>
              Samo moje
            </button>
          </div>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="w-16 shrink-0 text-xs text-gray-500 dark:text-gray-400">Stopnja:</span>
          {(['neobdelano'] as string[]).concat(PRIORITETE.map((n) => String(n))).map((v) => (
            <button key={v} onClick={() => setFilterPrio(preklopi(filterPrio, v))}
              className={clsx('px-2 py-1 rounded-lg text-xs font-medium border transition',
                filterPrio.indexOf(v) >= 0
                  ? 'bg-blue-600 border-blue-600 text-white'
                  : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-blue-400')}>
              {v === 'neobdelano' ? 'Neobdelano' : PRIO_LABEL[Number(v)]}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="w-16 shrink-0 text-xs text-gray-500 dark:text-gray-400">Status:</span>
          {STATUSI.map((s) => (
            <button key={s} onClick={() => nastaviStatus(preklopi(filterStatus, s))}
              className={clsx('px-2 py-1 rounded-lg text-xs font-medium border transition',
                filterStatus.indexOf(s) >= 0
                  ? 'bg-blue-600 border-blue-600 text-white'
                  : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-blue-400')}>
              {STATUS_LABEL[s]}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            {(filterPrio.length > 0 || filterStatus.length > 0) && (
              <button onClick={() => { setFilterPrio([]); nastaviStatus([]); }}
                className="px-2 py-1 rounded-lg text-xs text-gray-500 dark:text-gray-400 hover:text-blue-600 underline">
                počisti
              </button>
            )}
            <button onClick={() => { setVerzija((v) => v + 1); naloziStevec(); }}
              className="px-3 py-1.5 rounded-lg text-sm border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-blue-400 transition">
              Osveži
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* ------------------------------ SEZNAM ------------------------------ */}
        <div className="lg:col-span-1 space-y-2">
          {/* [PATCH napake-dialog-fe v4] vrtavka samo ob prvem/praznem nalaganju;
              samodejne osvezitve v ozadju (anketa) so tihe — brez utripanja */}
          {nalagam && seznam.length === 0 && (
            <div className="flex items-center gap-2 text-sm text-gray-400 px-1 py-3">
              <Loader2 className="w-4 h-4 animate-spin" /> Nalagam …
            </div>
          )}
          {!nalagam && seznam.length === 0 && (
            <div className="px-4 py-8 text-sm text-gray-400 text-center border border-dashed border-gray-200 dark:border-gray-700 rounded-xl">
              {zavihek === 'moje'
                ? (vidiServis && !samoMoje
                    ? 'V servisu še ni prijav.'
                    : 'Nimaš še nobene prijave.')
                : 'Ni prijav.'}
            </div>
          )}
          {seznam.map((p: any) => (
            <button key={p.id} onClick={() => setIzbranId(p.id)}
              className={clsx(
                'w-full text-left px-3 py-3 rounded-xl border transition',
                izbranId === p.id
                  ? 'border-blue-400 bg-blue-50 dark:bg-blue-950'
                  /* [PATCH napake-dialog-fe v2] rahlo amber ozadje neprebranih:
                     moje -> neprebrano, vse -> nov_odziv (celotno okence) */
                  : (zavihek === 'vse' ? p.nov_odziv : p.neprebrano)
                    ? 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950 hover:border-amber-300'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:border-blue-300'
              )}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-xs text-gray-500 dark:text-gray-400">{p.stevilka}</span>
                <div className="flex items-center gap-1">
                  {/* [PATCH napake-prioriteta-fe v1] oznaka prioritete — vidi jo tudi prijavitelj */}
                  <span className={clsx('px-1.5 py-0.5 rounded-full text-[11px] font-medium',
                                        PRIO_BARVA[prio(p.prioriteta)])}>
                    {PRIO_LABEL[prio(p.prioriteta)]}
                  </span>
                  <span className={clsx('px-1.5 py-0.5 rounded-full text-[11px] font-medium',
                                        STATUS_BARVA[p.status] || STATUS_BARVA.zavrnjeno)}>
                    {STATUS_LABEL[p.status] || p.status}
                  </span>
                </div>
              </div>
              <div className="mt-1 text-sm text-gray-900 dark:text-white line-clamp-2">
                {(p.opis || '').slice(0, 120)}
              </div>
              <div className="mt-1 flex items-center gap-2 text-[11px] text-gray-400">
                <span>{fmtCas(p.zadnja_aktivnost || p.created_at)}</span>
                {/* [PATCH napake-obseg-fe v1] barvna značka prijavitelja; strežnik
                    jo pošlje samo tam, kjer je smiselna (servisni obseg, /vse) */}
                {p.prijavitelj && (
                  <span className={clsx('px-1.5 py-0.5 rounded-full text-[11px] font-medium',
                                        barvaPrijavitelja(p.prijavitelj_barva))}>
                    {p.prijavitelj}
                  </span>
                )}
                {/* [PATCH napake-prioriteta-fe v1] mesto v vrsti IZKLJUČNO v zavihku
                    "Vse prijave" (samo superadmin). Prijavitelju se nikoli ne izriše —
                    mesto se lahko premakne nazaj in bi obljubljalo napredek, ki ga ni. */}
                {zavihek === 'vse' && p.mesto_v_vrsti && (
                  <span className="font-medium text-gray-500 dark:text-gray-300">
                    · {p.mesto_v_vrsti}. v vrsti
                  </span>
                )}
                {zavihek === 'vse' && p.st_prilog > 0 && <span>· {p.st_prilog} slik</span>}
                {p.neprebrano && (
                  <span className="ml-auto w-2 h-2 bg-blue-500 rounded-full" title="Nov odgovor" />
                )}
                {/* [PATCH napake-dialog-fe v1] neprebran odziv prijavitelja —
                    samo v zavihku "Vse prijave" (superadmin) */}
                {zavihek === 'vse' && p.nov_odziv && (
                  <span className={clsx('w-2 h-2 bg-amber-500 rounded-full', !p.neprebrano && 'ml-auto')} title="Nov odziv prijavitelja" />
                )}
              </div>
            </button>
          ))}
        </div>

        {/* --------------------------- PODROBNOSTI --------------------------- */}
        <div className="lg:col-span-2">
          {!izbranId && (
            <div className="px-4 py-12 text-sm text-gray-400 text-center border border-dashed border-gray-200 dark:border-gray-700 rounded-xl">
              Izberi prijavo na levi.
            </div>
          )}

          {izbranId && nalagamPodrobno && !podrobno && (
            <div className="flex items-center gap-2 text-sm text-gray-400 px-1 py-3">
              <Loader2 className="w-4 h-4 animate-spin" /> Nalagam …
            </div>
          )}

          {podrobno && (
            <div className="space-y-4 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-sm text-gray-500 dark:text-gray-400">{podrobno.stevilka}</span>
                  {/* [PATCH napake-prioriteta-fe v2] čas je premaknjen k številki prijave */}
                  <span className="text-xs text-gray-400">{fmtCas(podrobno.created_at)}</span>
                  {/* [PATCH napake-prijavitelj-fe v1] ime priimek + prijavni
                      e-mail prijavitelja. Streznik ju poslje SAMO superadminu,
                      zato pogoj po vlogi tukaj ni potreben. */}
                  {podrobno.prijavitelj && (
                    <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium',
                                          barvaPrijavitelja(podrobno.prijavitelj_barva))}>
                      {podrobno.prijavitelj}
                      {podrobno.prijavitelj_email && (
                        <span className="opacity-70"> · {podrobno.prijavitelj_email}</span>
                      )}
                    </span>
                  )}
                  <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium',
                                        PRIO_BARVA[prio(podrobno.prioriteta)])}>
                    {PRIO_LABEL[prio(podrobno.prioriteta)]}
                  </span>
                  <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium',
                                        STATUS_BARVA[podrobno.status] || STATUS_BARVA.zavrnjeno)}>
                    {STATUS_LABEL[podrobno.status] || podrobno.status}
                  </span>
                  {zavihek === 'vse' && podrobno.mesto_v_vrsti && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {podrobno.mesto_v_vrsti}. od {podrobno.odprtih_skupaj} v vrsti
                    </span>
                  )}
                </div>
              </div>

              <div>
                <div className={lbl}>Opis</div>
                <div className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">
                  {podrobno.opis}
                </div>
              </div>

              {slikeUrl.length > 0 && (
                <div>
                  <div className={lbl}>Posnetki zaslona</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {slikeUrl.map((u, i) => (
                      u ? (
                        <a key={i} href={u} target="_blank" rel="noreferrer"
                          className="block border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                          <img src={u} alt={'posnetek ' + (i + 1)} className="w-full" />
                        </a>
                      ) : (
                        <div key={i} className="px-3 py-6 text-xs text-gray-400 border border-dashed border-gray-200 dark:border-gray-700 rounded-lg text-center">
                          Slike ni bilo mogoče naložiti
                        </div>
                      )
                    ))}
                  </div>
                </div>
              )}

              {/* [PATCH napake-prioriteta-fe v2] Tehnični kontekst in klici na
                  strežnik so razvojni podatek. Strežnik jih prijavitelju sploh ne
                  pošlje (to je prava varovalka); tu bloka le ne izrišemo, da ne
                  kaže praznih pomišljajev. */}
              {jeSuperadmin && (
                <div>
                  <div className={lbl}>Kontekst</div>
                  <div className="text-xs text-gray-600 dark:text-gray-300 space-y-0.5">
                    <div>Ruta: <span className="font-mono">{K.ruta || '—'}</span></div>
                    <div>Podjetje: {K.podjetje || '—'}</div>
                    <div>Okno: {K.viewport || '—'} · zaslon {K.zaslon || '—'}</div>
                    <div>Čas pri uporabniku: {fmtCas(K.cas_odjemalca)}</div>
                    <div className="break-all">Brskalnik: {K.brskalnik || '—'}</div>
                  </div>
                </div>
              )}

              {jeSuperadmin && klici.length > 0 && (
                <div>
                  <div className={lbl}>Zadnji klici na strežnik ({klici.length})</div>
                  <div className="max-h-52 overflow-y-auto border border-gray-100 dark:border-gray-800 rounded-lg">
                    <table className="w-full text-[11px]">
                      <tbody>
                        {klici.map((z: any, i: number) => (
                          <tr key={i} className="border-b border-gray-100 dark:border-gray-800 last:border-0">
                            <td className="px-2 py-1 font-mono text-gray-400 whitespace-nowrap">
                              {String(z.cas || '').slice(11, 19)}
                            </td>
                            <td className="px-2 py-1 font-mono text-gray-500">{z.metoda}</td>
                            <td className="px-2 py-1 font-mono text-gray-700 dark:text-gray-300 break-all">{z.pot}</td>
                            <td className={clsx('px-2 py-1 font-mono text-right whitespace-nowrap',
                                                Number(z.status) >= 400 || typeof z.status === 'string'
                                                  ? 'text-red-600 dark:text-red-400 font-semibold'
                                                  : 'text-gray-400')}>
                              {z.status}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* [PATCH napake-dialog-fe v1] nit sporočil nadomešča prejšnji
                  enojni zeleni blok "Odgovor": zeleno = podpora (superadmin),
                  sivo = prijavitelj. Composer je skupen obema vlogama in
                  deluje tudi na rešenih/zavrnjenih prijavah ("ni popravljeno")
                  — status se s tem NE spremeni. */}
              {(podrobno.sporocila || []).length > 0 && (
                <div>
                  <div className={lbl}>Sporočila</div>
                  <div className="space-y-2">
                    {(podrobno.sporocila || []).map((m: any) => (
                      <div key={m.id}
                        className={clsx('rounded-lg border p-3',
                          m.je_superadmin
                            ? 'bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800'
                            : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700')}>
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className={clsx('text-xs font-medium',
                            m.je_superadmin
                              ? 'text-green-700 dark:text-green-300'
                              : 'text-gray-500 dark:text-gray-400')}>
                            {/* [PATCH napake-dialog-fe v2] prijavni e-mail avtorja;
                                legacy brez avtorja -> 'Podpora' */}
                            {m.avtor || (m.je_superadmin ? 'Podpora' : 'Prijavitelj')}
                          </span>
                          <span className="text-[11px] text-gray-400">{fmtCas(m.created_at)}</span>
                        </div>
                        <div className="text-sm text-gray-800 dark:text-gray-100 whitespace-pre-wrap">
                          {m.vsebina}
                        </div>
                        {(m.priloge || []).length > 0 && ( /* [PATCH napake-slike-filter v1] */
                          <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {m.priloge.map((a: any) => <SlikaPriloge key={a.id} pid={izbranId} aid={a.id} />)}
                          </div>)}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* [PATCH napake-dialog-fe v2] na koncano prijavo se ne odgovarja */}
              {podrobno.status === 'koncano' ? (
                <div className="px-3 py-2 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 text-xs text-gray-500 dark:text-gray-400">
                  Prijava je končana — odgovarjanje ni več mogoče.
                </div>
              ) : (
                <div>
                  <div className={lbl}>Novo sporočilo</div>
                  <textarea value={novoSporocilo} onChange={(e) => setNovoSporocilo(e.target.value)}
                    onPaste={(e) => { const fs = Array.from(e.clipboardData?.files || []); if (fs.length) { e.preventDefault(); dodajSlike(fs); } }}
                    rows={3} className={inp}
                    placeholder="Napišite sporočilo … (sliko lahko prilepite s Ctrl+V)" />
                  {noveSlike.length > 0 && ( /* [PATCH napake-slike-filter v1] predogled izbranih slik */
                    <div className="mt-2 flex flex-wrap gap-2">
                      {noveSlike.map((f, i) => <PredogledSlike key={i} f={f} odstrani={() => setNoveSlike((s) => s.filter((_, j) => j !== i))} />)}
                    </div>)}
                  <input ref={izbiraSlik} type="file" accept="image/*" multiple className="hidden"
                    onChange={(e) => { dodajSlike(Array.from(e.target.files || [])); if (izbiraSlik.current) izbiraSlik.current.value = ''; }} />
                  <div className="mt-2 flex items-center gap-2">
                    <button onClick={posljiSporocilo} disabled={posiljam || (!novoSporocilo.trim() && noveSlike.length === 0)}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-sm font-medium transition">
                      {posiljam && <Loader2 className="w-4 h-4 animate-spin" />}
                      Pošlji
                    </button>
                    <button type="button" onClick={() => izbiraSlik.current?.click()} disabled={posiljam || noveSlike.length >= 5}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-300 hover:border-blue-400 disabled:opacity-40">
                      <ImagePlus className="w-4 h-4" /> Dodaj sliko
                    </button>
                    <span className="text-[11px] text-gray-400">do 5 slik, največ 5 MB vsaka</span>
                  </div>
                </div>
              )}

              {jeSuperadmin && (
                <div className="pt-3 border-t border-gray-100 dark:border-gray-800 space-y-3">
                  <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                    Obravnava
                  </div>
                  {/* [PATCH napake-prioriteta-fe v1] prioriteta doloca vrstni red
                      reševanja; prijavitelj vidi njeno oznako, mesta v vrsti ne. */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className={lbl}>Status</label>
                      <select value={tStatus} onChange={(e) => setTStatus(e.target.value)} className={inp}>
                        {STATUSI.map((s) => (
                          <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={lbl}>Prioriteta</label>
                      <select value={String(tPrioriteta)}
                        onChange={(e) => setTPrioriteta(Number(e.target.value))} className={inp}>
                        {/* [PATCH napake-prioriteta-fe v2] 0 = vrni na neobdelano */}
                        <option value={0}>— neobdelano —</option>
                        {PRIORITETE.map((n) => (
                          <option key={n} value={n}>{PRIO_LABEL[n]}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className={lbl}>Interne opombe (uporabnik jih NE vidi)</label>
                    <textarea value={tOpombe} onChange={(e) => setTOpombe(e.target.value)}
                      rows={2} className={inp} />
                  </div>
                  <button onClick={shraniTriazo} disabled={shranjujem}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-sm font-medium transition">
                    {shranjujem ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Shrani
                  </button>
                  <div className="text-[11px] text-gray-400">
                    Sprememba statusa prižge značko pri prijavitelju; odgovori se pišejo v nit zgoraj.
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
